import React from "react";
import {
  Target, Path, SquaresFour, Hammer, Trophy, Flag, MapTrifold, Keyboard, DeviceMobile, Lightbulb, ChartLineUp, ListBullets, Waves,
  Cards, Infinity as InfinityIcon,
} from "@phosphor-icons/react";
import HowToPlayVideo from "@/components/HowToPlayVideo";
import WatchBoard from "@/components/WatchBoard";
import { BOARD_CHOICES } from "@/components/BoardChoice";
import { boardShape } from "@/lib/boardShapes";
import { useAuth } from "@/contexts/AuthContext";
import { myCharacter } from "@/lib/avatarConfig";

/**
 * Shared "How to Play" content — used by both the dedicated page (HowToPlay.jsx)
 * and the in-game collapsible drawer (HowToPlayDrawer.jsx). Self-contained, no
 * navigation chrome. The rules follow public/game/jangle.html; the diagrams
 * are drawn with the Watch page's board so they look like the game.
 */

// A diagram is a small crop of a board, written one string per row:
//   .  empty square          a-z  your (pink) tile     A-Z  opponent (blue) tile
//   ~  your Connector Tile   #    your wall tile       %    opponent wall tile
//   1  your base             2    opponent base        v ^  your / their base with the flag
//   +  Green POW square      -    Grey POW square      @    gem square    =  board wall
//   &  bridge / yellow square (Bridges)   _  river square    |  your bridge tile
//   :  sand square (Bridges 2.0)
//   *  start square          !    square a POW just blew up
// `bad` lists "r,c" of tiles that are in a non-word (red).
function parseDiagram(rows, bad = []) {
  const spec = {}, tiles = {}, marks = {}, base = { H: [], A: [] };
  rows.forEach((line, r) => {
    [...line].forEach((ch, c) => {
      const k = `${r},${c}`;
      if (ch === "1" || ch === "v") base.H.push([r, c]);
      if (ch === "2" || ch === "^") base.A.push([r, c]);
      if (ch === "v" || ch === "^") marks[k] = "flag";
      else if (ch === "+") spec[k] = "PINK";
      else if (ch === "-") spec[k] = "GREEN";
      else if (ch === "@") spec[k] = "SHIELD";
      else if (ch === "=") spec[k] = "WALL";
      else if (ch === "&") spec[k] = "BRIDGE";
      else if (ch === "_") spec[k] = "RIVER";
      else if (ch === ":") spec[k] = "SAND";
      else if (ch === "|") { spec[k] = "RIVER"; tiles[k] = { side: 0, letter: ch }; }
      else if (ch === "*") marks[k] = "start";
      else if (ch === "!") marks[k] = "hole";
      else if (ch === "~" || ch === "#") tiles[k] = { side: 0, letter: ch };
      else if (ch === "%") tiles[k] = { side: 1, letter: "#" };
      else if (/[a-z]/.test(ch)) tiles[k] = { side: 0, letter: ch.toUpperCase() };
      else if (/[A-Z]/.test(ch)) tiles[k] = { side: 1, letter: ch };
    });
  });
  bad.forEach((k) => { marks[k] = "bad"; });
  return { shape: { rows: rows.length, cols: rows[0].length, spec, base }, tiles, marks };
}

// wrap: draw the side edges purple, joined as on Infinity.
function Diagram({ rows, bad, caption, className = "", wrap = false }) {
  const { shape: grid, tiles, marks } = parseDiagram(rows, bad);
  const shape = wrap ? { ...grid, wrap: true } : grid;
  return (
    <figure className={`min-w-0 ${className}`}>
      <WatchBoard shape={shape} tiles={tiles} marks={marks} />
      {caption && <figcaption className="mt-1.5 text-[11px] text-center text-muted-foreground leading-snug">{caption}</figcaption>}
    </figure>
  );
}

// One square drawn as on the board, for the key of tiles and squares.
function Square({ ch }) {
  const { shape, tiles, marks } = parseDiagram([ch]);
  return <div className="w-11 flex-none"><WatchBoard shape={shape} tiles={tiles} marks={marks} className="!p-1 !rounded-lg" /></div>;
}

function Section({ icon: Icon, title, children }) {
  return (
    <section>
      <div className="flex items-center gap-2.5 mb-3">
        <Icon size={22} weight="fill" className="text-brand" />
        <h2 className="font-display text-2xl font-bold tracking-tight">{title}</h2>
      </div>
      <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">{children}</div>
    </section>
  );
}

const B = ({ children }) => <span className="font-bold text-foreground">{children}</span>;

function KeyRow({ items }) {
  return (
    <ul className="space-y-2">
      {items.map(([k, v]) => (
        <li key={k} className="flex items-start gap-3 p-3 rounded-lg bg-surface/60 border border-border">
          <span className="font-mono font-bold text-xs uppercase tracking-wider text-brand min-w-[92px] pt-0.5">{k}</span>
          <span className="text-muted-foreground">{v}</span>
        </li>
      ))}
    </ul>
  );
}

// The index at the top of How to play (Jack, 2026-10-27): every tile and
// square, drawn as on the board, with what it does.
const SQUARES = [
  ["j", "Your letter tile", "Pink on your screen. Your opponent's tiles are blue."],
  ["~", "Connector Tile", "A blank in your bag (9 per game). It joins words without being a letter."],
  ["#", "Wall tile", "Earned from a gem (5 to start on Bridges: there they only go right next to your base, and on Bridges 2.0 anywhere on your side of the river but the sand). Blocks a square, and POW can't blow it up."],
  ["+", "Green POW square", "Land on it to earn a Green POW card."],
  ["-", "Grey POW square", "Earns a Grey POW card, which only works once you've used a Green one."],
  ["@", "Gem square", "Land on it to earn a wall tile (Fortress, Fortress 2.0, Infinity, Firefly, The Wave)."],
  ["=", "Board wall", "Belongs to nobody (Firefly, The Wave, Infinity). Your words can pass through it, but your chain can't run from one board wall straight on to the next."],
  ["&", "Yellow square", "Bridges: each of your 3 earns a bridge tile."],
  ["_", "River", "Bridges only. Nothing but a bridge tile can go on it."],
  [":", "Sand", "Bridges 2.0 only: a row each side of the river. Letters can go on it, wall tiles can't."],
  ["|", "Bridge tile", "Bridges only. Lay it on the river, then build straight over it to cross. You can type a letter onto it."],
  ["1", "Base", "3×3, with your flag in the middle. Yours is at the bottom. Land a tile in theirs to win."],
];

const AWARDS = [
  ["🎯", "Use all your tiles in one turn", "Take 2 bonus tiles straight away."],
  ["🔨", "POW squares", "A POW card for each POW square you land on. Each square pays you once a game."],
  ["💎", "Gem squares", "A wall tile for each gem you land on, once a game per gem."],
  ["🛡️", "Fortress pro", "Collect all 4 gems on your half of Fortress, Fortress 2.0 or Infinity for 2 bonus wall tiles."],
  ["💥", "POW bonus", "On Fortress 2.0 and Infinity, land on all 3 green POW squares on your side (both of them on Bridges and Bridges 2.0) for 2 bonus Green POW cards, once a game."],
  ["🚩", "Into enemy territory", "A cheer the first time one of your tiles crosses into your opponent's half."],
];

export default function HowToPlayContent() {
  const { user } = useAuth();
  return (
    <div className="space-y-10" data-testid="how-to-play-content">
      <HowToPlayVideo character={myCharacter(user)} />

      <Section icon={ListBullets} title="Index">
        <p>What each tile and square does. There's more on each one further down.</p>
        <ul className="grid sm:grid-cols-2 gap-2" data-testid="how-to-play-index">
          {SQUARES.map(([ch, name, body]) => (
            <li key={name} className="flex items-center gap-3 p-2 rounded-lg bg-surface/60 border border-border">
              <Square ch={ch} />
              <div className="min-w-0">
                <div className="font-display font-bold text-foreground text-[13px]">{name}</div>
                <div className="text-[12px] leading-snug">{body}</div>
              </div>
            </li>
          ))}
        </ul>
      </Section>

      <Section icon={Target} title="The goal">
        <div className="flex gap-4 items-start">
          <div className="flex-1 space-y-3">
            <p>
              Two raiders, two bases, one tall board. Your base is at the bottom, your opponent's at the top. Build a
              chain of real words from your base up the board, and <B>land a tile inside their base</B> to break in and
              take their flag.
            </p>
            <p>
              You win the moment a tile of yours lands in their base, as long as it is joined to your chain and every
              word you've laid is real. If nobody breaks in after <B>15 turns each</B>, whoever got closest to the other
              base wins.
            </p>
          </div>
          <Diagram
            className="w-[36%] max-w-[150px] flex-none"
            rows={["..222..", "..2^2..", "..2h2..", "...e...", "...i...", "...s...", "...t..."]}
            caption="HEIST breaks in"
          />
        </div>
      </Section>

      <Section icon={Path} title="Your turn">
        <ol className="space-y-3">
          {[
            ["Start next to your base", "Your first word must use one of the squares touching your base (the yellow outlines). The base itself stays empty, and only one chain can come out of it."],
            ["Spell real words", "Type in any direction: up, down, left or right. Every line of two or more of your tiles must be a real word, read either way, crosswords included. Tiles in a non-word go red."],
            ["Stay joined", "Every new tile must join your chain. You can't type onto or past your opponent's tiles, so go around them, or POW them."],
            ["Play", "When there's no red, press Play. Your tiles lock in for the rest of the game and your hand is topped back up to 10."],
          ].map(([title, body], i) => (
            <li key={title} className="flex gap-3">
              <span className="flex-none w-6 h-6 rounded-full bg-brand/15 text-brand font-bold text-xs flex items-center justify-center mt-0.5">
                {i + 1}
              </span>
              <div>
                <div className="font-display font-bold text-foreground">{title}</div>
                <div>{body}</div>
              </div>
            </li>
          ))}
        </ol>
        <div className="grid grid-cols-2 gap-3 pt-1">
          <Diagram
            rows={[".......", "...t...", "...o...", "...a...", "..*d*..", ".*111*.", ".*1v1*.", ".*111*."]}
            caption="TOAD starts on a square touching the base"
          />
          <Diagram
            rows={[".......", "...t...", "..book.", "...a.x.", "...d...", "..111..", "..1v1..", "..111.."]}
            bad={["3,5"]}
            caption="BOOK crosses TOAD. KX isn't a word, so X is red"
          />
        </div>
        <p>
          Players take turns, and you can see how many are left under the board (Turn 3 of 15). The arrows beside it
          step back through the moves played so far.
        </p>
        <p>
          <B>Games are asynchronous.</B> There's no clock: play your turn whenever suits you, then just wait. You'll get
          a notification when your opponent has moved, and every game waiting on you shows in <B>Your games</B>, so you
          can have several going at once. Bots reply straight away. If you don't play your turn for 10 days your
          opponent wins, and you'll get a reminder on day 8.
        </p>
        <p>
          <B>Live Games</B> use chess clocks, and by default you both play <B>at the same time</B>: each turn you lay
          your word while your opponent lays theirs, and you see each other's tiles as they go down. Your clock runs
          until you press Play, and the next turn starts once you've both played. If you both go for the same square,
          the tile that gets there first keeps it and the other goes back to its hand. A tile you haven't played yet
          only holds its square while it's part of a real word: if it isn't, your opponent can lay over it and it goes
          back to your hand. Pick <B>Take turns</B> when you
          send the challenge to play one move each instead. Against a bot you can pick <B>Same time</B> too, under
          Turns when you choose the board: the bot lays its word a few seconds into each turn. In every Live Game,
          as in Raider Races, a wall tile stays where you lay it, so you double tap the square to lay one.
        </p>
      </Section>

      <Section icon={SquaresFour} title="Tiles and squares">
        <p>The Index under the video shows every tile and square. Here's how the trickier ones work.</p>
        <div className="grid grid-cols-2 gap-3 pt-1">
          <Diagram rows={[".......", "cat~dog", "......."]} caption="A Connector Tile joins CAT and DOG" />
          <Diagram rows={["...R...", "...A...", "...I...", "...D...", "...#..."]} caption="Your wall tile stops RAID" />
        </div>
        <p>
          A <B>Connector Tile</B> sits between words, so the letters either side of it don't have to spell one word
          together. A letter can sit alone beside a Connector only if it's part of a word the other way (A and I are
          fine on their own).
        </p>
        <p>
          A <B>wall tile</B> is a dead square: nobody can type onto it or through it, words can't start off it, and no
          POW can blow it up. Place it anywhere except on a POW square. Wall tiles you hold are extra, on top of your 10
          tiles.
        </p>
        <p>
          A <B>board wall</B> on Firefly and The Wave is different: it belongs to nobody, can't be picked up or blown up,
          and your words can run through it like a Connector.
        </p>
      </Section>

      <Section icon={Waves} title="Bridges: cross the river">
        <div className="flex gap-4 items-start">
          <div className="flex-1 space-y-3">
            <p>
              The narrow <B>Bridges</B> board has a <B>river</B> across the middle row. Nothing
              can go on the river except a <B>bridge tile</B>, and your words can only cross it straight over a bridge of
              your own: never sideways along the water, and never over your opponent's bridges.
            </p>
            <p>
              Land a tile on one of your <B>3 yellow squares</B> to earn a bridge tile (one each, once a game). Lay it on
              any river square, whenever you like, but not right beside another of your bridges. It links words like a
              Connector Tile, and POW can't blow it up. Once it's down you can also type a letter onto it: the letter
              counts in your words and the bridge stays underneath. On Bridges you start with <B>5 wall tiles</B>, which can only go on the squares right next to your base.
            </p>
            <p>
              Its only POW squares are the four corners. Land on <B>both green POW squares on your side</B> and you get
              a <B>bonus of 2 Green POW cards</B>, once a game.
            </p>
          </div>
          <Diagram
            className="w-[36%] max-w-[150px] flex-none"
            rows={["...s...", "...e...", "...a...", "___|___", "...n...", "...a...", "...p..."]}
            caption="Your bridge tile carries NAP across the river to SEA"
          />
        </div>
        <div className="flex gap-4 items-start">
          <div className="flex-1 space-y-3">
            <p>
              <B>Bridges 2.0</B> is Bridges with a <B>row of sand</B> on each side of the river, so the board is 2 rows
              longer. Sand squares take letters like any other square. Your <B>5 wall tiles</B> can go <B>anywhere on
              your side of the river</B>, but <B>not on the sand</B>.
            </p>
          </div>
          <Diagram
            className="w-[36%] max-w-[150px] flex-none"
            rows={["...s...", ":::e:::", "___|___", ":::n:::", "...a.#.", "...p..."]}
            caption="Letters on the sand are fine. Your wall goes on your side, off the sand"
          />
        </div>
      </Section>

      <Section icon={InfinityIcon} title="Infinity: the sides are joined">
        <div className="flex gap-4 items-start">
          <div className="flex-1 space-y-3">
            <p>
              <B>Infinity</B> is Fortress 2.0 with <B>5 board walls</B> across the middle of the centre row and <B>a wall in each corner</B> (its corner POW
              squares sit right beside them), and its
              left and right edges are <B>joined</B> (they're drawn purple). A word that runs off one side carries on
              from the other side, on the same row, and your chain carries on with it.
            </p>
            <p>
              Type off the edge and the next letter lands on the far side. A word that goes round the edge must be a
              real word read the whole way along. Your opponent can do the same, so they can come at your base round
              either side. The top and bottom edges don't join.
            </p>
          </div>
          <Diagram
            className="w-[36%] max-w-[150px] flex-none"
            wrap
            rows={[".......", "ow..arr", "......."]}
            caption="ARROW runs off the right edge and on from the left"
          />
        </div>
      </Section>

      <Section icon={Hammer} title="POW cards">
        <p>
          Land a tile on a POW square, as part of a real word joined to your chain, to earn a POW card. On your turn,
          tap the card, then tap a tile to blow it up. It goes back in its owner's bag, and the hole is yours to build
          through.
        </p>
        <div className="grid grid-cols-3 gap-2">
          <Diagram rows={[".......", ".STONE.", "...a...", "...r...", "...t..."]} caption="STONE blocks you" />
          <Diagram rows={[".......", ".ST!NE.", "...a...", "...r...", "...t..."]} caption="POW the O" />
          <Diagram rows={[".......", ".STcNE.", "...a...", "...r...", "...t..."]} caption="CART goes through" />
        </div>
        <ul className="space-y-1.5 list-disc pl-5">
          <li><B>Green POW</B> cards work straight away. <B>Grey POW</B> cards stay locked until you've used a Green one.</li>
          <li>You can POW your own locked-in tiles too, but not tiles you placed this turn (just pick those up).</li>
          <li>Wall tiles and board walls can't be blown up.</li>
          <li>Once its POW is used, the tile on that POW square can't be picked up. What's left of a broken word (ST and NE above) stays on the board.</li>
          <li>The <B>Opponent range</B> panel under their POW cards shows how many tiles they need to reach your base, counting the POWs they can use. It turns red when they're in reach.</li>
        </ul>
      </Section>

      <Section icon={Trophy} title="Awards">
        <p>
          At the start of each game the Game guide pops up: the awards on that board, and anything special about it (like
          Infinity's joined sides). Close it and it shrinks into the 📖 Guide button under the board, so you can check it
          again any time.
        </p>
        <ul className="space-y-2">
          {AWARDS.map(([ic, title, body]) => (
            <li key={title} className="flex items-start gap-3 p-3 rounded-lg bg-surface/60 border border-border">
              <span className="text-xl leading-none w-7 text-center flex-none">{ic}</span>
              <div>
                <div className="font-display font-bold text-foreground text-[13px]">{title}</div>
                <div className="text-[12px] leading-snug">{body}</div>
              </div>
            </li>
          ))}
        </ul>
        <p>
          A square's reward only counts while its tile is in a real word joined to your chain. Pick the tile back up
          and the reward goes back too, including any wall tiles it earned.
        </p>
      </Section>

      <Section icon={Flag} title="Ways to win">
        <ul className="space-y-2">
          {[
            ["Break in", "Land a tile in their base, joined to your chain, with no red words. Your raider runs your chain and takes their flag. A break-in is worth 1.5× the usual Elo."],
            ["Out of turns", "After 15 turns each, the player whose tile is closest to the other base wins. If that's level, the next closest tiles decide, then who has more tiles, then the player who went second."],
            ["Sealed off", "If your opponent is walled in, has no POW cards to use or earn, and can never get closer to your base than your closest tile, you can take the win on proximity early. You can also keep playing for a break-in."],
            ["Resign or leave", "If your opponent resigns, or leaves the game for too long, you win."],
            ["Unbroken chain (optional rule)", "Switch on Unbroken chain before a bot game or when you challenge someone, and a break-in only counts if one unbroken chain of your own tiles runs from your base into theirs. Tiles a POW has cut off don't count."],
          ].map(([k, v]) => (
            <li key={k} className="p-3 rounded-lg bg-surface/60 border border-border">
              <div className="font-display font-bold text-foreground">{k}</div>
              <div>{v}</div>
            </li>
          ))}
        </ul>
      </Section>

      <Section icon={Cards} title="Same tiles (optional)">
        <p>
          Switch on Same tiles before a bot game or when you challenge someone, and you both draw from the same
          bag in the same order. You start with the same tiles, and every new tile you draw is the one your
          opponent draws next too, so how you use them is what counts.
        </p>
        <p>
          So nobody can copy, your opponent's letters and Connector Tiles show blank, and so do their words, until the game ends.
          Their tiles still block and join words as usual.
        </p>
      </Section>

      <Section icon={MapTrifold} title="The boards">
        <p>You pick a board before every game, against bots too. Classic is the best place to start.</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {BOARD_CHOICES.map((b) => (
            <figure key={b.id} className="min-w-0">
              <WatchBoard shape={boardShape(b.id)} tiles={{}} letters={false} />
              <figcaption className="mt-2">
                <div className="font-display font-bold text-foreground text-[13px]">{b.name}</div>
                <div className="text-[11px] leading-snug">{b.blurb}</div>
              </figcaption>
            </figure>
          ))}
        </div>
      </Section>

      <Section icon={Keyboard} title="On a computer">
        <KeyRow items={[
          ["Click a square", "Puts the cursor there. Then type: the letter drops from your hand and the cursor moves on."],
          ["Arrow keys", "Move the cursor, or turn the typing direction."],
          ["1 and 2", "Place a Connector Tile (1) or a wall tile (2)."],
          ["Backspace", "Picks up the tile under the cursor (this turn's tiles only)."],
          ["Drag", "Drag a hand tile onto the board, drag this turn's tiles to move them, or drag tiles in your hand to rearrange it."],
          ["Space", "Play."],
        ]} />
      </Section>

      <Section icon={DeviceMobile} title="On a phone">
        <KeyRow items={[
          ["Tap a square", "Opens the Jangle keyboard. Letters you hold are highlighted (change this in Settings)."],
          ["Tap again", "Turns the typing direction. Jangle points it the way your word can grow."],
          ["Long-press", "Then drag, to move a hand tile onto the board or rearrange your hand."],
          ["Take back", "Returns your last placed tile to your hand. Tap again for the one before."],
          ["Play", "Locks in your turn."],
        ]} />
      </Section>

      <Section icon={Lightbulb} title="Word Check">
        <p>
          Not sure something's a word? Type it into <B>Word Check</B> beside the board to test it against the Jangle
          dictionary. There's no penalty for using it.
        </p>
      </Section>

      <Section icon={ChartLineUp} title="Elo">
        <p>
          Everyone starts at 1200. Games against people change your Elo, and beating a stronger player earns more.
          A break-in win earns 1.5× the usual gain. Bot games never change your Elo or your record, so practise freely.
        </p>
      </Section>
    </div>
  );
}
